import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { TwaProvider } from './app/TwaProvider';
import { HashGameSocketProvider } from './app/HashGameSocketProvider';
import { VisitTracker } from './app/VisitTracker';
import { installViewportHeightShim } from './app/viewportHeight';
import { router } from './router';
import App from './App';
import './index.css';
import '@/i18n';
import { queryClient } from '@/api/queryClient';
import { useAuthStore } from '@/store/auth';
import { enableOfflineCache } from '@/app/offlineCache';
import { primeDeviceModel } from '@/lib/deviceInfo';
import { loadLocale } from '@/i18n/locales/_index';
import { useLocaleStore } from '@/i18n/store';
import { markTelegramLaunchPayload } from '@/lib/tgLaunch';
import { referralCodeFromStartParam } from '@/lib/startParam';

// Stale-chunk recovery: after a redeploy the user's tab still references
// the old chunk hashes from its cached index.html, so the next lazy import
// 404s with "Failed to fetch dynamically imported module". Vite dispatches
// `vite:preloadError` for exactly this case — reload to pull a fresh
// index.html. The 10s timestamp guard prevents an infinite reload loop if
// the new build is genuinely broken, while still recovering from a later
// redeploy in a long-lived session.
window.addEventListener('vite:preloadError', () => {
  const key = 'vite-preload-reload-ts';
  const last = Number(sessionStorage.getItem(key) || 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(key, String(Date.now()));
  window.location.reload();
});

// Installed iPhone/iPad PWAs have WebKit bugs around first-launch standalone
// viewport sizing. Mark that mode early and swap full-height shells to CSS
// `100vh` there only; every other environment keeps the normal `100dvh` path.
installViewportHeightShim();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Root element #root not found in index.html — cannot bootstrap the app.");
}

// Referral-link capture: /r/:code → store code → redirect to home.
// Runs synchronously before React mounts so there is no flash of the wrong
// page. The browser navigates away immediately; React is not mounted at all
// for this hit, which also avoids triggering TanStack Query requests while
// the page is in the middle of an unload.
// ── Referral-link capture ─────────────────────────────────────────────────
const referralMatch = /^\/r\/([A-Za-z0-9_-]+)/.exec(window.location.pathname);
if (referralMatch) {
  try { sessionStorage.setItem('hg.referral-code', referralMatch[1]); } catch { /* storage full / private */ }
  window.location.replace(`/#/r/${referralMatch[1]}`);
} else {
  // ── Telegram initData capture ──────────────────────────────────────────
  // Telegram Web passes initData in the URL hash as `#tgWebAppData=...`.
  // Our hash router uses `#/route` so it would immediately overwrite this
  // hash on mount, discarding the data. Capture and store it synchronously
  // here before React mounts, then clean the hash so the router starts at `#/`.
  try {
    const h = window.location.hash;
    if (h && !h.startsWith('#/')) {
      const params = new URLSearchParams(h.slice(1)); // drop leading '#'
      const raw = params.get('tgWebAppData');
      if (raw) {
        // `URLSearchParams.get()` already decodes the outer tgWebAppData hash
        // value once. Decoding again corrupts the signed inner initData
        // payload (for example `user=%7B...%7D` becomes `user={...}`), which
        // breaks Telegram HMAC verification on the backend.
        sessionStorage.setItem('hg.tg-init-data', raw);
        // Marks the blob as belonging to THIS load, so the automatic sign-in
        // can trust it without having to guess from the window's display mode.
        markTelegramLaunchPayload();
      }
      // tgWebAppStartParam is the startapp deep-link code passed as a
      // top-level hash param when the mini-app is opened via
      // t.me/bot/app?startapp=CODE in Telegram's "open in web" flow.
      // Store it before the hash is cleared so HomeRoute can redirect to
      // signup even if the TG SDK hasn't populated initDataUnsafe yet.
      const startParamFromHash = referralCodeFromStartParam(params.get('tgWebAppStartParam'));
      if (startParamFromHash) {
        try { sessionStorage.setItem('hg.referral-code', startParamFromHash); } catch { /* storage unavailable */ }
      } else if (raw) {
        // Some clients omit the standalone tgWebAppStartParam hash param but
        // still embed `start_param=CODE` inside the signed tgWebAppData string.
        // Recover it here so the code is captured synchronously, before React.
        try {
          const sp = referralCodeFromStartParam(new URLSearchParams(raw).get('start_param'));
          if (sp) sessionStorage.setItem('hg.referral-code', sp);
        } catch { /* URL API / storage unavailable */ }
      }
      // Restore a clean hash so the router can initialise normally.
      window.history.replaceState(null, '', '/#/');
    }
  } catch { /* sessionStorage / URL API unavailable */ }

  // Telegram startapp referral capture: when the mini-app is opened via
  // t.me/bot/game?startapp=CODE, store the code before React mounts so
  // HomeRoute finds it synchronously on first render and redirects to sign up.
  try {
    const startParam = referralCodeFromStartParam(
      window.Telegram?.WebApp?.initDataUnsafe?.start_param,
    );
    if (startParam) {
      sessionStorage.setItem('hg.referral-code', startParam);
    }
  } catch { /* storage unavailable */ }

  void primeDeviceModel();

  // A returning user is already signed in before any auth path runs, so
  // `setSession` never fires for them. Without this they would be the one
  // group that never gets a worker.
  if (useAuthStore.getState().isAuthed()) enableOfflineCache();

  // Load the user's locale chunk before mounting React so useT() always has
  // the correct strings on the very first render — no English flash for
  // non-English users. English is bundled statically and resolves instantly;
  // other locales load from the service worker cache (<5 ms on repeat visits)
  // or from the network (~50-200 ms on first visit).
  void (async () => {
    await loadLocale(useLocaleStore.getState().locale);

    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <TwaProvider>
          <QueryClientProvider client={queryClient}>
            <HashGameSocketProvider>
              <VisitTracker />
              <App>
                <RouterProvider router={router} />
              </App>
            </HashGameSocketProvider>
          </QueryClientProvider>
        </TwaProvider>
      </React.StrictMode>,
    );
  })();
}
